import axios from 'axios';
import { getBackendBaseUrl } from '../utils/configUtils';

// Create an Axios instance with default config optimized for mobile
// Use localhost for development
// Build a dynamic API base from environment (REACT_APP_API_URL) or sensible default
const buildApiBaseUrl = () => {
  const base = getBackendBaseUrl();
  // Ensure no trailing slash before appending /api/v2
  const normalized = base.replace(/\/$/, '');
  return `${normalized}/api/v2`;
};

const API_BASE_URL = buildApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Extended timeout for mobile networks
  timeout: 30000,
});

// Create a direct Axios instance with full URL for cases where proxy won't work
// (redirects, cross-origin requests, etc.)
const directApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Add auth token to requests and prevent caching for GET requests
const addAuthToken = (config) => {
  // Ensure headers object exists
  config.headers = config.headers || {};
  
  // Add auth token if available
  const token = localStorage.getItem('cubxly_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Ensure Content-Type is set for POST/PUT/PATCH requests
  const method = config.method?.toLowerCase();
  if (['post', 'put', 'patch'].includes(method)) {
    config.headers['Content-Type'] = config.headers['Content-Type'] || 'application/json';
  }
  
  // Smart cache-busting: Only add cache-busting when explicitly requested
  if (method === 'get') {
    const url = config.url || '';
    const isImageEndpoint = url.includes('/files/') || 
                           url.includes('/images/') || 
                           url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i);
    
    // Only add cache-busting if explicitly requested via _cacheBust parameter
    const shouldCacheBust = config.params && config.params._cacheBust;
    
    if (!isImageEndpoint && shouldCacheBust) {
      // Remove the _cacheBust flag and add timestamp
      delete config.params._cacheBust;
      config.params['_t'] = new Date().getTime();
      console.log(`[API] Making GET request to ${url} (with cache-busting)`);
    } else {
      console.log(`[API] Making GET request to ${url} (no cache-busting)`);
    }
  }
  
  return config;
};

// Request interceptor for adding auth token to proxy-based API
api.interceptors.request.use(
  addAuthToken,
  (error) => Promise.reject(error)
);

// Request interceptor for adding auth token to direct API
directApi.interceptors.request.use(
  addAuthToken,
  (error) => Promise.reject(error)
);

// Response handler for successful responses
const handleSuccessResponse = (response) => {
  // Log successful responses in development for debugging
  if (process.env.NODE_ENV === 'development') {
    console.log(`API Response from ${response.config.url}:`, {
      status: response.status,
      statusText: response.statusText,
      data: response.data
    });
  }
  return response;
};

// Response interceptor for handling errors in proxy-based API
api.interceptors.response.use(
  handleSuccessResponse,
  // Error handler for both API instances
  (error) => {
    // Enhanced error handling
    if (error.response) {
      const { status, data } = error.response;
      console.error(`API Error (${status}):`, data);
      
      // Handle different error status codes
      switch (status) {
        case 401: // Unauthorized
          localStorage.removeItem('cubxly_token');
          // Only redirect if not already on login page to avoid redirection loops
          if (!window.location.pathname.includes('login')) {
            window.location.href = '/login';
          }
          break;
          
        case 403: // Forbidden
          console.error('Access forbidden to this resource');
          break;
          
        case 404: // Not found
          console.error('Resource not found');
          break;
          
        case 500: // Server error
          console.error('Server error occurred');
          break;
          
        default:
          console.error(`Unhandled status code: ${status}`);
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('No response received from server:', error.request);
      
      // Check if offline
      if (!navigator.onLine) {
        console.warn('Device appears to be offline');
        // We could dispatch an offline notification here
      }
    } else {
      // Error in setting up the request
      console.error('Error setting up request:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Add the same response interceptor to the direct API
directApi.interceptors.response.use(
  handleSuccessResponse,
  // Reuse the same error handler
  (error) => {
    // Enhanced error handling
    if (error.response) {
      const { status, data } = error.response;
      console.error(`API Error (${status}):`, data);
      
      // Handle different error status codes
      switch (status) {
        case 401: // Unauthorized
          localStorage.removeItem('cubxly_token');
          // Only redirect if not already on login page to avoid redirection loops
          if (!window.location.pathname.includes('login')) {
            window.location.href = '/login';
          }
          break;
          
        case 403: // Forbidden
          console.error('Access forbidden to this resource');
          break;
          
        case 404: // Not found
          console.error('Resource not found');
          break;
          
        case 500: // Server error
          console.error('Server error occurred');
          break;
          
        default:
          console.error(`Unhandled status code: ${status}`);
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('No response received from server:', error.request);
      
      // Check if offline
      if (!navigator.onLine) {
        console.warn('Device appears to be offline');
        // We could dispatch an offline notification here
      }
    } else {
      // Error in setting up the request
      console.error('Error setting up request:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Admin API instance (uses /api/admin instead of /api/v2)
const buildAdminApiBaseUrl = () => {
  const base = getBackendBaseUrl();
  const normalized = base.replace(/\/$/, '');
  return `${normalized}/api/admin`;
};

const adminApi = axios.create({
  baseURL: buildAdminApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Add auth token to admin API requests
adminApi.interceptors.request.use(addAuthToken);

// Factory function to get admin API (ensures fresh token)
export const getAdminApi = () => adminApi;

// Export both API instances
export { directApi, adminApi };
export default api;
